import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowRight, Crown, Sparkles, Search, FileText, TrendingUp, Link2, Settings, BarChart, Users } from "lucide-react";
import { getRelatedTools, getToolsByCategory } from "@/data/toolsData";

// Icon mapping
const iconMap = {
  Search, FileText, TrendingUp, Link2, Settings, BarChart, Users, Sparkles, Crown
};

export default function RelatedTools({ currentToolId, category, limit = 4 }) {
  // Get related tools based on current tool or category
  const relatedTools = currentToolId 
    ? getRelatedTools(currentToolId, limit)
    : getToolsByCategory(category).slice(0, limit);

  if (!relatedTools || relatedTools.length === 0) return null;

  return (
    <div className="mt-12 border-t pt-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-xl font-bold text-slate-900">Related Tools</h3>
          <p className="text-sm text-slate-500 mt-1">Try these tools next to boost your workflow</p>
        </div>
        <Link 
          to="/tools" 
          className="text-sm text-indigo-600 hover:text-indigo-700 font-medium flex items-center gap-1"
        >
          View All Tools <ArrowRight className="w-4 h-4" />
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {relatedTools.map(tool => {
          const Icon = iconMap[tool.icon] || Search;
          const toolUrl = `${tool.path}?tool=${tool.id}`;
          
          return (
            <Link
              key={tool.id}
              to={toolUrl}
              className="group"
            >
              <Card className="p-4 h-full hover:shadow-lg hover:border-indigo-200 transition-all duration-300 hover:-translate-y-1">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-indigo-50 to-violet-50 flex items-center justify-center flex-shrink-0 group-hover:from-indigo-100 group-hover:to-violet-100 transition-colors">
                    <Icon className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-semibold text-slate-900 group-hover:text-indigo-600 transition-colors truncate">
                        {tool.name}
                      </h4>
                      {tool.isPremium && <Crown className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />}
                      {tool.isNew && <Badge className="bg-emerald-100 text-emerald-700 text-[10px] px-1.5">NEW</Badge>}
                    </div>
                    <p className="text-xs text-slate-500 line-clamp-2">{tool.description}</p>
                  </div>
                </div>
                
                <div className="mt-3 flex items-center text-xs text-indigo-600 font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                  Try this tool <ArrowRight className="w-3 h-3 ml-1" />
                </div>
              </Card>
            </Link>
          );
        })}
      </div>

      {/* Suggested Workflow */}
      <div className="mt-8 p-6 bg-gradient-to-r from-indigo-50 via-violet-50 to-purple-50 rounded-2xl">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-white shadow-sm flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-indigo-600" />
          </div>
          <div>
            <h4 className="font-semibold text-slate-900">Suggested Workflow</h4>
            <p className="text-sm text-slate-600">Complete these steps for best results</p>
          </div>
        </div>
        
        <div className="flex flex-wrap gap-3">
          {relatedTools.slice(0, 3).map((tool, index) => (
            <Link
              key={tool.id}
              to={`${tool.path}?tool=${tool.id}`}
              className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm hover:shadow-md transition-shadow"
            >
              <span className="w-5 h-5 rounded-full bg-indigo-100 text-indigo-600 text-xs font-bold flex items-center justify-center">
                {index + 1}
              </span>
              <span className="text-sm font-medium text-slate-700">{tool.name}</span>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
